import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Button,
  Close,
  Dropdown,
  SurfaceCard,
  Trash,
} from '@react-apps/design-system';
import type { ExamPreviewQuestion } from '../../domain/entities/content.entity';
import {
  useBulkDeleteQuestions,
  useRemoveExamQuestionReference,
  useRefetchExamPreview,
  useReorderExamQuestion,
} from '../hooks/use-exams';
import { readQuestionBody } from '../exam-body';
import { sanitizeQuestionPreview } from '@react-apps/content-editor';
import {
  ANSWER_KEY_ANCHOR,
  questionAnchor,
  useQuestionSpotlight,
} from '../hooks/use-question-spotlight';
import type { ExamQuestionMove } from '../hooks/use-exam-question-move';
import {
  ContentStatusBadge,
  getApiErrorMessage,
  notifyDone,
  notifyFailed,
} from '../display';
import { QuestionBodyView } from './QuestionBodyView';
import { ConfirmModal } from './ConfirmModal';
import { EditLockNotice } from './EditLockNotice';
import { IconButton } from '@/shared/components/icon-button';
import { QuestionFlagBadges } from './QuestionFlagBadges';
import { QuestionClassificationChips } from './QuestionClassificationChips';

/**
 * O id opaco só serve para colar num ticket ou numa query. Storage/clipboard
 * bloqueado (contexto não-seguro, permissão negada) não deve derrubar nada — o
 * pior caso é o clique não fazer efeito.
 */
async function copyId(questionId: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(questionId);
  } catch {
    // Sem clipboard não há o que fazer aqui, e não é erro do usuário.
  }
}

/**
 * Card de uma questão dentro do preview da prova: leitura, posição e o que
 * age sobre a prova (seleção, remover do simulado, excluir).
 *
 * **A prova não edita a questão** (decisão 11 do épico content-admin): o card
 * não tem lápis, e "Abrir questão" leva à ficha dela, onde enunciado,
 * classificação e comentário se editam juntos, sob o lock da questão. O que
 * fica aqui é a posição, porque ela é da prova, e não da questão.
 *
 * **Em simulado (`isMockExam`) não há excluir nem seleção em massa** — as
 * duas ações agem sobre a questão na prova dona (excluir a tira de todo lugar;
 * transferir troca o dono). O que o simulado oferece é "remover do simulado",
 * que desfaz só o apontador. Ver primum-monolith
 * `docs/reference/modules/content.md` § Dono vs. apontador.
 */
export function QuestionPreviewCard({
  entry,
  examId,
  selected,
  onToggleSelected,
  move,
  isMockExam = false,
}: {
  entry: ExamPreviewQuestion;
  examId: string;
  isMockExam?: boolean;
  selected: boolean;
  onToggleSelected: (questionId: string) => void;
  /** O "Mover" da página: um lock da prova para todos os cards. */
  move: ExamQuestionMove;
}) {
  const { register, spotlight } = useQuestionSpotlight();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isUnlinkOpen, setIsUnlinkOpen] = useState(false);
  const remove = useBulkDeleteQuestions();
  const unlink = useRemoveExamQuestionReference(examId);
  const body = readQuestionBody(entry.question.body);

  const moving = move.movingId === entry.question.id;
  const isDraft = entry.question.status === 'DRAFT';

  return (
    <div ref={register(questionAnchor(entry.position))}>
      <SurfaceCard className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            {/* Não existe `Checkbox` no design-system — input nativo é o
                fallback já usado nos outros painéis deste módulo. */}
            {!isMockExam && (
              <input
                type="checkbox"
                checked={selected}
                onChange={() => onToggleSelected(entry.question.id)}
                aria-label={`Selecionar a questão ${entry.position}`}
              />
            )}
            {/* Item 5: o título identifica a questão E é o caminho de volta
                para o gabarito. A posição é só isto — texto (item 12): um
                campo numérico focável no header de 80 cards, cuja
                consequência é renumerar a prova inteira, era o controle mais
                fácil de disparar sem querer da tela. Mudar é pelo "Mover". */}
            <button
              type="button"
              onClick={() => spotlight(ANSWER_KEY_ANCHOR)}
              title="Voltar para o gabarito"
              className="text-(--color-text-primary) hover:text-(--color-accent) text-sm font-semibold"
            >
              Questão {entry.position}
            </button>
            <ContentStatusBadge status={entry.question.status} />
            {/* As três marcações editoriais (anulada, passível de recurso,
                desatualizada) numa peça só — ver QuestionFlagBadges. */}
            <QuestionFlagBadges body={body} />
            <span className="text-(--color-text-secondary) text-xs">
              {body?.format === 'ESSAY' ? 'Dissertativa' : 'Múltipla escolha'}
            </span>
            {/* A classificação fica no header, ao lado do status: numa revisão
                de 80 questões é o que se confere de relance. Só se lê: ela
                muda na ficha da questão. */}
            <QuestionClassificationChips
              classifications={entry.classifications}
            />
          </div>
          <div className="flex items-center gap-1">
            <Link
              to={`/content/items/${entry.question.id}`}
              // A ficha sabe que veio da prova e troca o "Voltar" pelo
              // breadcrumb. O id é só esse sinal: o link do breadcrumb sai da
              // aresta da questão.
              state={{ fromExamId: examId }}
              aria-label={`Abrir a questão ${entry.position}`}
              className="text-(--color-accent) px-1.5 text-xs font-medium hover:underline"
            >
              Abrir questão
            </Link>
            {!moving && (
              <Button
                type="button"
                variant="ghost"
                size="xs"
                aria-label={`Mover a questão ${entry.position}`}
                // O `POST` do lock da prova está no ar: um segundo clique não
                // pede outro.
                disabled={move.lock.acquiring}
                onClick={() => move.begin(entry.question.id)}
              >
                Mover
              </Button>
            )}
            {isMockExam ? (
              <IconButton
                label={`Remover a questão ${entry.position} do simulado`}
                icon={<Close size="sm" />}
                variant="danger"
                onClick={() => {
                  unlink.reset();
                  setIsUnlinkOpen(true);
                }}
              />
            ) : (
              <IconButton
                label={`Excluir a questão ${entry.position}`}
                icon={<Trash size="sm" />}
                variant="danger"
                onClick={() => {
                  remove.reset();
                  setIsDeleteOpen(true);
                }}
              />
            )}
            {/* O que é raro sai do header: o id opaco serve a suporte/debug,
                não a quem está revisando. Anular é pelas Marcações do form,
                na ficha da questão, ou pela grade do gabarito. */}
            <Dropdown
              align="right"
              items={[
                {
                  id: 'copy-id',
                  label: 'Copiar o id da questão',
                  onClick: () => void copyId(entry.question.id),
                },
                isMockExam
                  ? {
                      id: 'remove-reference',
                      label: 'Remover do simulado',
                      onClick: () => {
                        unlink.reset();
                        setIsUnlinkOpen(true);
                      },
                    }
                  : {
                      id: 'delete',
                      label: 'Excluir a questão',
                      onClick: () => {
                        remove.reset();
                        setIsDeleteOpen(true);
                      },
                    },
              ]}
              trigger={
                <span
                  role="img"
                  aria-label={`Mais ações da questão ${entry.position}`}
                  title="Mais ações"
                  className="text-(--color-text-secondary) hover:bg-(--color-bg-secondary) hover:text-(--color-text-primary) flex h-7 w-7 items-center justify-center rounded text-base leading-none"
                >
                  ⋯
                </span>
              }
            />
          </div>
        </div>

        {moving && <MoveControl entry={entry} examId={examId} move={move} />}

        {body === null ? (
          <p className="text-sm text-red-600">
            Corpo da questão em formato inesperado — abra a questão para
            inspecionar.
          </p>
        ) : (
          <>
            <QuestionBodyView body={body} />
            {entry.officialComment && (
              <OfficialCommentView comment={entry.officialComment} />
            )}
          </>
        )}

        <ConfirmModal
          open={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={() =>
            remove.mutate([entry.question.id], {
              onSuccess: (results) => {
                setIsDeleteOpen(false);
                // O back decide entre apagar de vez e manter o registro; dizer
                // qual dos dois foi é o que a pessoa precisa saber depois.
                notifyDone(
                  results[0]?.mode === 'TOMBSTONED'
                    ? `Questão ${entry.position} saiu da prova (registro preservado)`
                    : `Questão ${entry.position} excluída`,
                );
              },
              // Troca o toast cru da rede global (mesmo id) pela frase
              // traduzida — ex.: `QUESTION_REFERENCED`, rascunho que algum
              // simulado aponta.
              onError: (err) =>
                notifyFailed(err, `Questão ${entry.position} não foi excluída`),
            })
          }
          title={`Excluir a questão ${entry.position}?`}
          description={
            isDraft
              ? 'A questão está em rascunho: será removida de vez, junto com o vínculo com a prova.'
              : 'A questão já foi publicada: ela sai da prova — e de todo simulado que aponta para ela —, mas o registro é preservado (soft delete) porque pode estar referenciado em atividades de aluno.'
          }
          confirmLabel="Excluir"
          loading={remove.isPending}
          error={remove.error}
        />

        <ConfirmModal
          open={isUnlinkOpen}
          onClose={() => setIsUnlinkOpen(false)}
          onConfirm={() =>
            unlink.mutate(entry.question.id, {
              onSuccess: () => {
                setIsUnlinkOpen(false);
                notifyDone(`Questão ${entry.position} saiu do simulado`);
              },
              onError: (err) =>
                notifyFailed(
                  err,
                  `Questão ${entry.position} não saiu do simulado`,
                ),
            })
          }
          title={`Remover a questão ${entry.position} do simulado?`}
          description="Só o simulado deixa de usá-la: a questão continua na prova de origem e em outros simulados. As seguintes sobem uma posição."
          confirmLabel="Remover do simulado"
          loading={unlink.isPending}
          error={unlink.error}
        />
      </SurfaceCard>
    </div>
  );
}

/**
 * O comentário oficial no card. Some quando a questão não tem — não há
 * "nenhum comentário" a exibir em 80 cards; quem quer criar abre a questão.
 *
 * O texto vem do back já com `<img>` no lugar do token, então é a mesma
 * sanitização de leitura do corpo da questão.
 */
function OfficialCommentView({
  comment,
}: {
  comment: NonNullable<ExamPreviewQuestion['officialComment']>;
}) {
  return (
    <div className="border-(--color-border) space-y-1 border-t pt-3">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-(--color-text-secondary) text-xs font-medium uppercase tracking-wide">
          Comentário oficial
        </span>
        {comment.author && (
          <span className="text-(--color-text-secondary) text-xs">
            por {comment.author}
          </span>
        )}
      </div>
      <div
        className="rich-text rich-text-read text-(--color-text-primary) text-sm"
        dangerouslySetInnerHTML={{
          __html: sanitizeQuestionPreview(comment.text),
        }}
      />
    </div>
  );
}

/**
 * O "Mover" aberto num card: o aviso do lock da prova, e, com o lock, o campo
 * da posição. Mover solta o lock; Cancelar também.
 *
 * A piscada na posição efetiva (o back satura no fim da prova) é o que diz
 * para onde o card foi: a lista reordena e ele sai de onde a pessoa olhava.
 */
function MoveControl({
  entry,
  examId,
  move,
}: {
  entry: ExamPreviewQuestion;
  examId: string;
  move: ExamQuestionMove;
}) {
  const questionId = entry.question.id;
  const reorder = useReorderExamQuestion(examId);
  const refetchPreview = useRefetchExamPreview(examId);
  const { spotlight } = useQuestionSpotlight();
  const [position, setPosition] = useState(String(entry.position));
  const { lock } = move;
  const noticeId = `edit-lock-exam-${examId}`;

  const target = Number(position);
  const valid = Number.isInteger(target) && target >= 1;
  const changed = valid && target !== entry.position;

  const commit = async () => {
    if (!changed) return;
    try {
      const links = await reorder.mutateAsync({
        questionId,
        newPosition: target,
      });
      // Esperada: o card só existe na posição nova quando a lista já foi
      // re-renderizada com ela.
      await refetchPreview();
      const movedTo =
        links.find((link) => link.targetContentId === questionId)?.position ??
        target;
      notifyDone(`Questão ${entry.position} → posição ${movedTo}`);
      move.end();
      spotlight(questionAnchor(movedTo));
      // O controle some e o card muda de lugar: o foco segue o card, no
      // "Mover" dele, em vez de cair no `body`.
      requestAnimationFrame(() =>
        document
          .querySelector<HTMLButtonElement>(
            `[aria-label="Mover a questão ${movedTo}"]`,
          )
          ?.focus(),
      );
    } catch (err) {
      notifyFailed(err, `Questão ${entry.position} não foi movida`);
    }
  };

  return (
    <div className="space-y-2">
      <EditLockNotice
        id={noticeId}
        state={lock.state}
        subject="A ordem da prova"
        keepsText={false}
        retrying={lock.acquiring}
        onRetry={() => move.begin(questionId)}
        onExit={move.end}
      />
      {!lock.editing && (
        <div className="flex flex-wrap items-center gap-3">
          {lock.acquiring && (
            <p className="text-(--color-text-secondary) text-xs">
              Conferindo se alguém já está mexendo na ordem da prova…
            </p>
          )}
          {/* Travada por outra pessoa, o aviso só oferece tentar de novo: a
              saída de quem desistiu de mover é esta. */}
          <Button type="button" variant="ghost" size="xs" onClick={move.end}>
            Cancelar
          </Button>
        </div>
      )}
      {lock.editing && (
        <form
          className="flex flex-wrap items-center gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void commit();
          }}
        >
          <label className="text-(--color-text-secondary) flex items-center gap-2 text-xs">
            Mover para a posição
            {/* Input nativo: o `Input` do design-system embute label/wrapper. */}
            <input
              type="number"
              min={1}
              // Aberto pelo "Mover", que some: o foco vem para o campo.
              autoFocus
              value={position}
              disabled={reorder.isPending}
              onChange={(e) => setPosition(e.target.value)}
              title="A prova é renumerada 1..N a partir daqui — a questão é movida, as do meio deslocam"
              className="border-(--color-border) bg-(--color-bg-secondary) text-(--color-text-primary) h-7 w-14 rounded border text-center text-sm font-semibold tabular-nums disabled:opacity-50"
            />
          </label>
          <Button
            type="submit"
            size="xs"
            // Começa pelo texto visível ("Mover"), para o comando de voz, e
            // difere do "Mover a questão N" do header. Em andamento, o
            // "Movendo…" visível é o nome.
            aria-label={
              reorder.isPending
                ? undefined
                : `Mover a questão ${entry.position} para a posição ${position}`
            }
            disabled={!changed || reorder.isPending}
          >
            {reorder.isPending ? 'Movendo…' : 'Mover'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="xs"
            disabled={reorder.isPending}
            onClick={move.end}
          >
            Cancelar
          </Button>
          {reorder.error != null && (
            <span className="text-xs text-red-600" role="alert">
              {getApiErrorMessage(reorder.error, 'Falha ao mover')}
            </span>
          )}
        </form>
      )}
    </div>
  );
}
